import React from "react";
import { Box, Typography, Divider, List, ListItemButton, ListItemIcon, ListItemText, Avatar } from "@mui/material";
import { ChartNoAxesCombined, Palette, CreditCard } from 'lucide-react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import Cookies from 'js-cookie';
import { useUser } from '../providers/user-provider';
import { useNavigate } from 'react-router-dom';

export interface UserActionsProps {
    user: { name: string; email: string; avatarUrl?: string };
    onAccount: () => void;
    onCreateCompany: () => void;
    onHelp: () => void;
    onLogout: () => void;
    onBillingSubscription: () => void;
    isAdmin?: boolean;
    companyButtonRef?: React.RefObject<HTMLElement | null>;
}

const UserActions: React.FC<UserActionsProps> = ({
    user,
    onAccount,
    onCreateCompany,
    onHelp,
    onLogout,
    onBillingSubscription,
    isAdmin = false,
    companyButtonRef,
}) => {
    const { user: currentUser } = useUser();
    const navigate = useNavigate();

    const getCurrentUserRole = (): string => {
        if (!currentUser?.entityId) return 'User';
        try {
            const companiesCookie = Cookies.get('entities');
            const companies = companiesCookie ? JSON.parse(companiesCookie) : [];
            const currentCompany = companies.find((c: any) => c.entityId === currentUser.entityId);
            return currentCompany?.role === 'admin' ? 'Admin' : 'Read Only';
        } catch {
            return 'User';
        }
    };

    const getCurrentCompanyName = (): string => {
        if (!currentUser?.entityId) return 'Company Name';
        try {
            const companiesCookie = Cookies.get('entities');
            const companies = companiesCookie ? JSON.parse(companiesCookie) : [];
            const currentCompany = companies.find((c: any) => c.entityId === currentUser.entityId);
            return currentCompany?.entityName || 'Company Name';
        } catch {
            return 'Company Name';
        }
    };

    const canManageBilling = getCurrentUserRole() === 'Admin';
    const headerName = user.name || user.email || 'Unknown User';
    const headerInitials = (
        (user.name || '').split(/\s+/).filter(Boolean).map(part => part[0]).slice(0, 2).join('')
        || (user.email || '?')[0]
    ).toUpperCase();
    // Swapped from the original layout (company name on top, role + email
    // below): the user's own name/email leads, company name moved down
    // alongside the role.
    const headerSubtitle = `${getCurrentUserRole()} • ${getCurrentCompanyName()}`;

    return (
        <Box sx={{ minWidth: 280, borderRadius: 2, boxShadow: 3, p: 2, bgcolor: 'background.paper' }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Avatar
                    src={user.avatarUrl || undefined}
                    alt={headerName}
                    sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 700, bgcolor: 'var(--color-primary)', color: '#fff' }}
                >
                    {headerInitials}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600} fontSize={16} sx={{ color: 'var(--color-default-text)' }}>{headerName}</Typography>
                    {/* Email under the name (unless the name already is the email). */}
                    {user.email && user.email !== headerName && (
                        <Typography
                            fontSize={12.5}
                            title={user.email}
                            sx={{ color: 'var(--color-default-text)', opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}
                        >
                            {user.email}
                        </Typography>
                    )}
                    <Typography fontSize={12} sx={{ color: 'var(--color-default-text)', opacity: 0.7 }}>{headerSubtitle}</Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List disablePadding>
                <ListItemButton onClick={onAccount} sx={{ borderRadius: 1, height: 40 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <PersonOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={<Typography fontWeight={500} fontSize={14}>Account</Typography>} />
                </ListItemButton>
                <ListItemButton onClick={() => navigate('/color-scheme')} sx={{ borderRadius: 1, height: 40 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <Palette size={16}/>
                    </ListItemIcon>
                    <ListItemText primary={<Typography fontWeight={500} fontSize={14}>Personalisation</Typography>} />
                </ListItemButton>
                {canManageBilling && (
                    <ListItemButton onClick={onBillingSubscription} sx={{ borderRadius: 1, height: 40 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <CreditCard size={16}/>
                        </ListItemIcon>
                        <ListItemText primary={<Typography fontWeight={500} fontSize={14}>Billing & Subscription</Typography>} />
                    </ListItemButton>
                )}
                <ListItemButton onClick={onCreateCompany} sx={{ borderRadius: 1, height: 40 }} ref={companyButtonRef as any}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <ChartNoAxesCombined size={16}/>
                    </ListItemIcon>
                    <ListItemText primary={<Typography fontWeight={500} fontSize={14}>Switch Company</Typography>} />
                </ListItemButton>
            </List>
            <Divider sx={{ my: 1 }} />
            <List disablePadding>
                <ListItemButton onClick={onLogout} sx={{ borderRadius: 1, height: 40 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={<Typography fontWeight={500} fontSize={14}>Logout</Typography>} />
                </ListItemButton>
            </List>
        </Box>
    );
};

export default UserActions;
